/**
 * Local roster fixtures — replicate the API/DB roster responses without any
 * backend call, so the roster UI can be built and tested against a range of
 * states (full, open slots, injuries, elite stats, empty).
 *
 * Each variation's `roster` object matches the exact shape returned by
 * rosterService.getRoster():
 *   { players, bench, minorLeague, activeLineup, lineupsByDate, lastUpdated }
 * and getFixtureRoster() returns the same envelope: { success, roster }.
 *
 * Wiring lives in src/utils/rosterManager.js (getRosterForDate /
 * saveRosterForDate short-circuit here when USE_ROSTER_FIXTURES is true).
 * Flip USE_ROSTER_FIXTURES to false to go back to the real backend.
 */

// Master switch. When true, roster reads/writes use these fixtures instead of
// the GraphQL/DynamoDB backend (no network calls for rosters).
export const USE_ROSTER_FIXTURES = true;

/* ------------------------------------------------------------------ *
 * Player factories — produce objects in the app's stored player shape *
 * ------------------------------------------------------------------ */

let _seq = 0;
const nextId = (prefix) => `${prefix}_fx_${(++_seq).toString(36)}`;

const splitName = (name) => {
  const parts = name.split(' ');
  return { firstName: parts[0] || '', lastName: parts.slice(1).join(' ') || '' };
};

// Hitter: s = { avg, hr, rbi, sb, r }
const H = (name, team, positions, s, status = 'active') => {
  const id = nextId('h');
  const { firstName, lastName } = splitName(name);
  // Sample salary-cap / contract metadata (the app has no real salary data yet).
  // Deterministic from stats so columns vary without churning across renders.
  const salary = `$${Math.max(2, Math.min(60, Math.round((s.hr || 0) * 1.4 + 6)))}`;
  const contract = status === 'MiLB' ? 'Minors' : (s.hr >= 25 ? '3 yr' : s.hr >= 15 ? '2 yr' : '1 yr');
  // Derive season hits/at-bats consistent with the batting average (for the H/AB column).
  const avgNum = parseFloat(s.avg) || 0;
  const atBats = 360 + Math.round(((s.r || 0) + (s.rbi || 0)) * 0.85);
  const hits = Math.round(avgNum * atBats);
  return {
    id,
    playerId: id,
    name,
    firstName,
    lastName,
    position: positions[0],
    positions,
    eligiblePositions: positions,
    team,
    teamId: null,
    photoUrl: null,
    status,
    salary,
    contract,
    stats: {
      battingAverage: s.avg,
      homeRuns: s.hr,
      rbi: s.rbi,
      stolenBases: s.sb,
      runs: s.r,
      hits,
      atBats,
      wins: 0,
      saves: 0,
      strikeouts: 0,
      era: 0,
      whip: 0,
    },
  };
};

// Pitcher: s = { w, sv, k, era, whip }
const P = (name, team, positions, s, status = 'active') => {
  const id = nextId('p');
  const { firstName, lastName } = splitName(name);
  return {
    id,
    playerId: id,
    name,
    firstName,
    lastName,
    position: positions[0],
    positions,
    eligiblePositions: positions,
    team,
    teamId: null,
    photoUrl: null,
    status,
    stats: {
      battingAverage: 0,
      homeRuns: 0,
      rbi: 0,
      stolenBases: 0,
      runs: 0,
      wins: s.w,
      saves: s.sv,
      strikeouts: s.k,
      era: s.era,
      whip: s.whip,
    },
  };
};

// Assemble a roster in the DB response shape. `players` is the union of every
// player across the lineup/bench/minors (matches how saved rosters store them).
const makeRoster = ({ activeLineup, bench = [], minorLeague = [] }) => {
  const lineupPlayers = [];
  Object.values(activeLineup).forEach((v) => {
    if (Array.isArray(v)) v.forEach((p) => p && lineupPlayers.push(p));
    else if (v) lineupPlayers.push(v);
  });
  return {
    players: [...lineupPlayers, ...bench, ...minorLeague],
    activeLineup,
    bench,
    minorLeague,
    lineupsByDate: null,
    lastUpdated: '2026-07-30',
  };
};

const emptyLineup = () => ({
  C: null,
  '1B': null,
  '2B': null,
  '3B': null,
  SS: null,
  MI: null,
  CI: null,
  OF: [],
  UTIL: [],
  P: [],
});

/* ------------------------------------------------------------------ *
 * Variation builders (each returns a fresh roster template)          *
 * ------------------------------------------------------------------ */

// FULL — a complete, legal roster: every active slot filled, healthy stars,
// a couple of bench bats and a minor-leaguer. The happy path.
const buildFull = () =>
  makeRoster({
    activeLineup: {
      C: H('Andre Willis', 'ATL', ['C', 'UTIL'], { avg: '.271', hr: 18, rbi: 61, sb: 2, r: 48 }),
      '1B': H('Cole Bannister', 'SF', ['1B', 'UTIL'], { avg: '.301', hr: 24, rbi: 78, sb: 5, r: 71 }),
      '2B': H('Raymond Mbeki', 'CHC', ['2B', 'SS', 'UTIL'], { avg: '.276', hr: 12, rbi: 49, sb: 19, r: 58 }),
      '3B': H('Victor Pérez', 'NYM', ['3B', 'UTIL'], { avg: '.264', hr: 20, rbi: 63, sb: 3, r: 54 }),
      SS: H('Hideki Sato', 'SD', ['SS', '2B', 'UTIL'], { avg: '.289', hr: 9, rbi: 42, sb: 12, r: 51 }),
      MI: H('Quentin Foster', 'SEA', ['2B', 'SS', 'UTIL'], { avg: '.244', hr: 10, rbi: 40, sb: 15, r: 46 }),
      CI: H('Isaiah Davies', 'PHI', ['1B', '3B', 'UTIL'], { avg: '.258', hr: 17, rbi: 55, sb: 1, r: 49 }),
      OF: [
        H('Marcus Rodríguez', 'LAD', ['CF', 'OF', 'UTIL'], { avg: '.318', hr: 27, rbi: 82, sb: 21, r: 84 }),
        H('Jaylen Brooks', 'ATL', ['LF', 'OF', 'UTIL'], { avg: '.295', hr: 18, rbi: 60, sb: 10, r: 68 }),
        H('Sung-min Kim', 'TEX', ['RF', 'OF', 'UTIL'], { avg: '.272', hr: 22, rbi: 65, sb: 4, r: 59 }),
        H('Diego Navarro', 'ARI', ['CF', 'OF', 'UTIL'], { avg: '.279', hr: 14, rbi: 51, sb: 17, r: 62 }),
      ],
      UTIL: [
        H('Owen Castellano', 'HOU', ['1B', 'UTIL'], { avg: '.288', hr: 30, rbi: 88, sb: 2, r: 74 }),
        H('Theo Lindqvist', 'CHC', ['LF', 'OF', 'UTIL'], { avg: '.266', hr: 15, rbi: 52, sb: 8, r: 55 }),
      ],
      P: [
        P('Danny Okafor', 'SF', ['SP', 'P'], { w: 12, sv: 0, k: 171, era: '2.74', whip: '1.04' }),
        P('Brett Holloway', 'MIN', ['SP', 'P'], { w: 10, sv: 0, k: 148, era: '3.18', whip: '1.11' }),
        P('Lars Pettersen', 'MIL', ['SP', 'P'], { w: 8, sv: 0, k: 122, era: '3.90', whip: '1.24' }),
        P('Gabriel Ruiz', 'BAL', ['SP', 'P'], { w: 11, sv: 0, k: 160, era: '3.05', whip: '1.09' }),
        P('Nathan Cole', 'TB', ['SP', 'P'], { w: 9, sv: 0, k: 139, era: '3.42', whip: '1.16' }),
        P('Theo Marsh', 'CHC', ['RP', 'P'], { w: 4, sv: 22, k: 68, era: '2.41', whip: '0.98' }),
        P('Danh Nguyen', 'BOS', ['RP', 'P'], { w: 3, sv: 14, k: 61, era: '3.02', whip: '1.12' }),
        P('Marcus Delgado', 'HOU', ['RP', 'P'], { w: 2, sv: 9, k: 55, era: '3.28', whip: '1.19' }),
        P('Bruno Toussaint', 'MIA', ['RP', 'P'], { w: 5, sv: 3, k: 72, era: '2.88', whip: '1.07' }),
      ],
    },
    bench: [
      H('Emilio Vargas', 'NYM', ['RF', 'OF', 'UTIL'], { avg: '.261', hr: 16, rbi: 53, sb: 6, r: 50 }),
      P('Owen Grant', 'CLE', ['SP', 'P'], { w: 7, sv: 0, k: 118, era: '3.55', whip: '1.20' }),
    ],
    minorLeague: [
      H('Santino Rossi', 'BAL', ['SS', '2B'], { avg: '.312', hr: 12, rbi: 44, sb: 22, r: 51 }, 'MiLB'),
    ],
  });

// OPEN SLOTS — several unfilled active slots (open C, one OF, two pitcher
// slots) and a thin bench. Exercises empty-slot rendering and "needs players".
const buildOpenSlots = () =>
  makeRoster({
    activeLineup: {
      C: null,
      '1B': H('Cole Bannister', 'SF', ['1B', 'UTIL'], { avg: '.301', hr: 24, rbi: 78, sb: 5, r: 71 }),
      '2B': H('Raymond Mbeki', 'CHC', ['2B', 'SS', 'UTIL'], { avg: '.276', hr: 12, rbi: 49, sb: 19, r: 58 }),
      '3B': null,
      SS: H('Hideki Sato', 'SD', ['SS', '2B', 'UTIL'], { avg: '.289', hr: 9, rbi: 42, sb: 12, r: 51 }),
      MI: null,
      CI: H('Isaiah Davies', 'PHI', ['1B', '3B', 'UTIL'], { avg: '.258', hr: 17, rbi: 55, sb: 1, r: 49 }),
      OF: [
        H('Marcus Rodríguez', 'LAD', ['CF', 'OF', 'UTIL'], { avg: '.318', hr: 27, rbi: 82, sb: 21, r: 84 }),
        H('Jaylen Brooks', 'ATL', ['LF', 'OF', 'UTIL'], { avg: '.295', hr: 18, rbi: 60, sb: 10, r: 68 }),
        // 2 of 4 OF slots open
      ],
      UTIL: [
        H('Owen Castellano', 'HOU', ['1B', 'UTIL'], { avg: '.288', hr: 30, rbi: 88, sb: 2, r: 74 }),
        // 1 of 2 UTIL open
      ],
      P: [
        P('Danny Okafor', 'SF', ['SP', 'P'], { w: 12, sv: 0, k: 171, era: '2.74', whip: '1.04' }),
        P('Brett Holloway', 'MIN', ['SP', 'P'], { w: 10, sv: 0, k: 148, era: '3.18', whip: '1.11' }),
        P('Theo Marsh', 'CHC', ['RP', 'P'], { w: 4, sv: 22, k: 68, era: '2.41', whip: '0.98' }),
        P('Danh Nguyen', 'BOS', ['RP', 'P'], { w: 3, sv: 14, k: 61, era: '3.02', whip: '1.12' }),
        // 5 of 9 P slots open
      ],
    },
    bench: [
      H('Emilio Vargas', 'NYM', ['RF', 'OF', 'UTIL'], { avg: '.261', hr: 16, rbi: 53, sb: 6, r: 50 }),
    ],
    minorLeague: [],
  });

// INJURIES — a healthy core plus a spread of injury states: DTD in the lineup,
// IL-10 and IL-60 shown, a rehabbing minor-leaguer. Exercises injury chips.
const buildInjuries = () =>
  makeRoster({
    activeLineup: {
      C: H('Andre Willis', 'ATL', ['C', 'UTIL'], { avg: '.247', hr: 14, rbi: 52, sb: 2, r: 41 }, 'DTD'),
      '1B': H('Cole Bannister', 'SF', ['1B', 'UTIL'], { avg: '.301', hr: 24, rbi: 78, sb: 5, r: 71 }),
      '2B': H('Raymond Mbeki', 'CHC', ['2B', 'SS', 'UTIL'], { avg: '.276', hr: 12, rbi: 49, sb: 19, r: 58 }),
      '3B': H('Victor Pérez', 'NYM', ['3B', 'UTIL'], { avg: '.264', hr: 20, rbi: 63, sb: 3, r: 54 }, 'DTD'),
      SS: H('Hideki Sato', 'SD', ['SS', '2B', 'UTIL'], { avg: '.289', hr: 9, rbi: 42, sb: 12, r: 51 }),
      MI: H('Quentin Foster', 'SEA', ['2B', 'SS', 'UTIL'], { avg: '.244', hr: 10, rbi: 40, sb: 15, r: 46 }),
      CI: H('Isaiah Davies', 'PHI', ['1B', '3B', 'UTIL'], { avg: '.258', hr: 17, rbi: 55, sb: 1, r: 49 }),
      OF: [
        H('Marcus Rodríguez', 'LAD', ['CF', 'OF', 'UTIL'], { avg: '.318', hr: 27, rbi: 82, sb: 21, r: 84 }),
        H('Jaylen Brooks', 'ATL', ['LF', 'OF', 'UTIL'], { avg: '.295', hr: 18, rbi: 60, sb: 10, r: 68 }),
        H('Sung-min Kim', 'TEX', ['RF', 'OF', 'UTIL'], { avg: '.272', hr: 22, rbi: 65, sb: 4, r: 59 }),
        H('Diego Navarro', 'ARI', ['CF', 'OF', 'UTIL'], { avg: '.279', hr: 14, rbi: 51, sb: 17, r: 62 }),
      ],
      UTIL: [
        H('Owen Castellano', 'HOU', ['1B', 'UTIL'], { avg: '.288', hr: 30, rbi: 88, sb: 2, r: 74 }),
        H('Theo Lindqvist', 'CHC', ['LF', 'OF', 'UTIL'], { avg: '.266', hr: 15, rbi: 52, sb: 8, r: 55 }),
      ],
      P: [
        P('Danny Okafor', 'SF', ['SP', 'P'], { w: 12, sv: 0, k: 171, era: '2.74', whip: '1.04' }),
        P('Brett Holloway', 'MIN', ['SP', 'P'], { w: 10, sv: 0, k: 148, era: '3.18', whip: '1.11' }),
        P('Lars Pettersen', 'MIL', ['SP', 'P'], { w: 8, sv: 0, k: 122, era: '3.90', whip: '1.24' }, 'DTD'),
        P('Gabriel Ruiz', 'BAL', ['SP', 'P'], { w: 11, sv: 0, k: 160, era: '3.05', whip: '1.09' }),
        P('Nathan Cole', 'TB', ['SP', 'P'], { w: 9, sv: 0, k: 139, era: '3.42', whip: '1.16' }),
        P('Theo Marsh', 'CHC', ['RP', 'P'], { w: 4, sv: 22, k: 68, era: '2.41', whip: '0.98' }),
        P('Danh Nguyen', 'BOS', ['RP', 'P'], { w: 3, sv: 14, k: 61, era: '3.02', whip: '1.12' }),
        P('Marcus Delgado', 'HOU', ['RP', 'P'], { w: 2, sv: 9, k: 55, era: '3.28', whip: '1.19' }),
        P('Bruno Toussaint', 'MIA', ['RP', 'P'], { w: 5, sv: 3, k: 72, era: '2.88', whip: '1.07' }),
      ],
    },
    bench: [
      H('Kenji Watanabe', 'SD', ['CF', 'OF', 'UTIL'], { avg: '.233', hr: 6, rbi: 24, sb: 7, r: 30 }, 'IL-10'),
      P('Owen Grant', 'CLE', ['SP', 'P'], { w: 6, sv: 0, k: 96, era: '3.55', whip: '1.19' }, 'IL-60'),
    ],
    minorLeague: [
      H('Santino Rossi', 'BAL', ['SS', '2B'], { avg: '.312', hr: 12, rbi: 44, sb: 22, r: 51 }, 'MiLB'),
    ],
  });

// STARS — an elite, category-crushing roster. Exercises number formatting and
// leader emphasis with strong values across the board.
const buildStars = () =>
  makeRoster({
    activeLineup: {
      C: H('Andre Willis', 'ATL', ['C', 'UTIL'], { avg: '.318', hr: 31, rbi: 96, sb: 4, r: 79 }),
      '1B': H('Owen Castellano', 'HOU', ['1B', 'UTIL'], { avg: '.331', hr: 42, rbi: 118, sb: 3, r: 101 }),
      '2B': H('Raymond Mbeki', 'CHC', ['2B', 'SS', 'UTIL'], { avg: '.305', hr: 24, rbi: 77, sb: 38, r: 96 }),
      '3B': H('Victor Pérez', 'NYM', ['3B', 'UTIL'], { avg: '.312', hr: 35, rbi: 104, sb: 6, r: 88 }),
      SS: H('Hideki Sato', 'SD', ['SS', '2B', 'UTIL'], { avg: '.329', hr: 28, rbi: 90, sb: 27, r: 99 }),
      MI: H('Quentin Foster', 'SEA', ['2B', 'SS', 'UTIL'], { avg: '.298', hr: 21, rbi: 71, sb: 41, r: 92 }),
      CI: H('Isaiah Davies', 'PHI', ['1B', '3B', 'UTIL'], { avg: '.307', hr: 38, rbi: 109, sb: 2, r: 85 }),
      OF: [
        H('Marcus Rodríguez', 'LAD', ['CF', 'OF', 'UTIL'], { avg: '.341', hr: 44, rbi: 121, sb: 33, r: 118 }),
        H('Jaylen Brooks', 'ATL', ['LF', 'OF', 'UTIL'], { avg: '.322', hr: 33, rbi: 95, sb: 19, r: 103 }),
        H('Sung-min Kim', 'TEX', ['RF', 'OF', 'UTIL'], { avg: '.309', hr: 40, rbi: 112, sb: 8, r: 90 }),
        H('Diego Navarro', 'ARI', ['CF', 'OF', 'UTIL'], { avg: '.315', hr: 26, rbi: 82, sb: 29, r: 97 }),
      ],
      UTIL: [
        H('Cole Bannister', 'SF', ['1B', 'UTIL'], { avg: '.327', hr: 37, rbi: 107, sb: 6, r: 94 }),
        H('Theo Lindqvist', 'CHC', ['LF', 'OF', 'UTIL'], { avg: '.301', hr: 29, rbi: 86, sb: 14, r: 88 }),
      ],
      P: [
        P('Danny Okafor', 'SF', ['SP', 'P'], { w: 19, sv: 0, k: 248, era: '1.98', whip: '0.86' }),
        P('Brett Holloway', 'MIN', ['SP', 'P'], { w: 17, sv: 0, k: 221, era: '2.24', whip: '0.94' }),
        P('Lars Pettersen', 'MIL', ['SP', 'P'], { w: 16, sv: 0, k: 209, era: '2.41', whip: '0.98' }),
        P('Gabriel Ruiz', 'BAL', ['SP', 'P'], { w: 18, sv: 0, k: 233, era: '2.12', whip: '0.90' }),
        P('Nathan Cole', 'TB', ['SP', 'P'], { w: 15, sv: 0, k: 198, era: '2.55', whip: '1.01' }),
        P('Theo Marsh', 'CHC', ['RP', 'P'], { w: 6, sv: 41, k: 94, era: '1.62', whip: '0.79' }),
        P('Danh Nguyen', 'BOS', ['RP', 'P'], { w: 5, sv: 33, k: 88, era: '1.90', whip: '0.85' }),
        P('Marcus Delgado', 'HOU', ['RP', 'P'], { w: 4, sv: 27, k: 81, era: '2.05', whip: '0.92' }),
        P('Bruno Toussaint', 'MIA', ['RP', 'P'], { w: 7, sv: 12, k: 99, era: '2.18', whip: '0.95' }),
      ],
    },
    bench: [
      H('Emilio Vargas', 'NYM', ['RF', 'OF', 'UTIL'], { avg: '.294', hr: 25, rbi: 79, sb: 11, r: 84 }),
      H('Kenji Watanabe', 'SD', ['CF', 'OF', 'UTIL'], { avg: '.288', hr: 19, rbi: 66, sb: 24, r: 81 }),
    ],
    minorLeague: [
      H('Santino Rossi', 'BAL', ['SS', '2B'], { avg: '.336', hr: 18, rbi: 58, sb: 31, r: 67 }, 'MiLB'),
    ],
  });

// EMPTY — a brand-new, undrafted roster. Exercises the fully-empty UI state.
const buildEmpty = () =>
  makeRoster({ activeLineup: emptyLineup(), bench: [], minorLeague: [] });

/* ------------------------------------------------------------------ *
 * Variation registry                                                  *
 * ------------------------------------------------------------------ */

// Order matters: index 0 is the default for a team with no explicit mapping.
export const VARIATIONS = [
  { key: 'full', label: 'Full roster', build: buildFull },
  { key: 'stars', label: 'All-stars', build: buildStars },
  { key: 'injuries', label: 'With injuries', build: buildInjuries },
  { key: 'openSlots', label: 'Open slots', build: buildOpenSlots },
  { key: 'empty', label: 'Empty (undrafted)', build: buildEmpty },
];

export const VARIATION_KEYS = VARIATIONS.map((v) => v.key);
const VARIATION_MAP = Object.fromEntries(VARIATIONS.map((v) => [v.key, v]));

/* ------------------------------------------------------------------ *
 * Session cache + selection                                           *
 * ------------------------------------------------------------------ */

const deepClone = (obj) =>
  typeof structuredClone === 'function' ? structuredClone(obj) : JSON.parse(JSON.stringify(obj));

// In-memory cache so in-session edits (drag-to-move, add/drop) persist across
// remounts without a backend. Keyed by cacheKey (league:team:date).
const sessionCache = new Map();

// Deterministic team -> variation mapping so browsing different teams shows a
// spread of states. Simple stable hash over the id string.
const hashString = (str) => {
  let h = 0;
  const s = String(str || '');
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

export const pickVariationForTeam = (teamId) => {
  if (!teamId) return VARIATION_KEYS[0];
  return VARIATION_KEYS[hashString(teamId) % VARIATION_KEYS.length];
};

// Per-team variation override (set by the My Roster variation switcher).
const overrideStorageKey = (teamId) => `tjfb-roster-fixture-variation-${teamId}`;

export const getVariationOverride = (teamId) => {
  if (!teamId) return null;
  try {
    const v = localStorage.getItem(overrideStorageKey(teamId));
    return v && VARIATION_MAP[v] ? v : null;
  } catch {
    return null;
  }
};

export const setVariationOverride = (teamId, variationKey) => {
  if (!teamId) return;
  try {
    if (variationKey && VARIATION_MAP[variationKey]) {
      localStorage.setItem(overrideStorageKey(teamId), variationKey);
    } else {
      localStorage.removeItem(overrideStorageKey(teamId));
    }
    // Drop any cached rosters for this team so the new variation is rebuilt.
    for (const key of Array.from(sessionCache.keys())) {
      if (key.includes(`:${teamId}:`)) sessionCache.delete(key);
    }
  } catch {
    /* localStorage unavailable — variation just won't persist */
  }
};

// ---- Global test-data "season phase" mode (replaces the per-team variation) ----
export const TEST_DATA_MODES = [
  { key: 'pre-draft', label: 'Pre-draft' },
  { key: 'mid-season', label: 'Mid-season' },
  { key: 'after-season', label: 'After season' },
];
const TEST_DATA_MODE_KEY = 'tjfb-test-data-mode';
const DEFAULT_TEST_DATA_MODE = 'mid-season';

export const getTestDataMode = () => {
  try {
    const m = localStorage.getItem(TEST_DATA_MODE_KEY);
    return TEST_DATA_MODES.some((x) => x.key === m) ? m : DEFAULT_TEST_DATA_MODE;
  } catch {
    return DEFAULT_TEST_DATA_MODE;
  }
};

export const setTestDataMode = (mode) => {
  try {
    if (TEST_DATA_MODES.some((x) => x.key === mode)) localStorage.setItem(TEST_DATA_MODE_KEY, mode);
    sessionCache.clear(); // rebuild every roster for the new phase
  } catch {
    /* localStorage unavailable — mode just won't persist */
  }
};

// Each phase's roster shape: pre-draft = undrafted (empty), otherwise a full
// drafted roster. This replaces the old per-team variation switcher.
const MODE_VARIATION = { 'pre-draft': 'empty', 'mid-season': 'full', 'after-season': 'full' };

/**
 * Resolve which roster variation applies — now driven by the global season-phase
 * test-data mode rather than a per-team pick.
 */
export const resolveVariationKey = () => MODE_VARIATION[getTestDataMode()] || 'full';

/**
 * Fixture equivalent of rosterService.getRoster(). Returns { success, roster }.
 * Rosters are cached per cacheKey so edits stick within a session.
 */
export const getFixtureRoster = (cacheKey, variationKey) => {
  if (!sessionCache.has(cacheKey)) {
    const variation = VARIATION_MAP[variationKey] || VARIATIONS[0];
    // Clone so edits to one team's cached roster never mutate the shared template.
    sessionCache.set(cacheKey, deepClone(variation.build()));
  }
  return { success: true, roster: sessionCache.get(cacheKey) };
};

/**
 * Fixture equivalent of rosterService.saveRoster(). Updates the in-memory cache
 * only — no backend, no persistence beyond the session.
 */
export const saveFixtureRoster = (cacheKey, roster) => {
  sessionCache.set(cacheKey, deepClone(roster));
  return { success: true };
};

/* ------------------------------------------------------------------ *
 * Sample player news                                                  *
 * ------------------------------------------------------------------ */

// Injury-flavored headlines for players carrying a status; general beat notes
// otherwise. Each returns { title, content } given a player.
const INJURY_NEWS = {
  DTD: [
    { title: '{name} listed as day-to-day', content: '{name} is dealing with a minor issue and was held out of the starting lineup. The team calls him day-to-day and expects him back within a game or two.' },
    { title: '{name} a game-time decision', content: 'Manager says {name} ({team}) is a game-time decision. He took part in early work and could be available off the bench tonight.' },
  ],
  'IL-10': [
    { title: '{name} placed on the 10-day IL', content: '{name} landed on the 10-day injured list, retroactive to earlier this week. An imaging update is expected in the coming days; fantasy managers should seek a short-term replacement.' },
  ],
  'IL-60': [
    { title: '{name} moved to the 60-day IL', content: '{name} ({team}) has been transferred to the 60-day injured list. He is not expected back until late in the season — a roster stash at best.' },
  ],
  MiLB: [
    { title: '{name} raking at Triple-A', content: '{name} continues to force the issue in the minors, hitting well over his last ten games. A call-up could come if the parent club needs a spark.' },
  ],
};

const HITTER_NEWS = [
  { title: '{name} stays hot at the plate', content: '{name} ({team}) extended his hitting streak with another multi-hit night. He is squaring the ball up and hitting near the top of the order.' },
  { title: '{name} homers again', content: '{name} went deep for the second time in three games. The power surge has him among the team leaders over the past two weeks.' },
  { title: '{name} swipes another bag', content: '{name} added a stolen base and continues to run aggressively, a nice source of cheap speed for fantasy lineups.' },
  { title: '{name} slots into the cleanup spot', content: 'The manager moved {name} up in the order, a vote of confidence that should mean more RBI chances going forward.' },
];

const PITCHER_NEWS = [
  { title: '{name} dominant in latest start', content: '{name} ({team}) tossed six-plus strong innings with a bunch of strikeouts, lowering his ERA and cementing his rotation spot.' },
  { title: '{name} named this week’s starter', content: '{name} is lined up for two starts this week, boosting his streaming value in all formats.' },
  { title: '{name} locks down another save', content: '{name} slammed the door for his latest save and remains the clear ninth-inning option.' },
];

const isPitcher = (p) => {
  const pos = (p.positions || [p.position] || []).join(',');
  return /SP|RP|(^|,)P(,|$)/.test(pos);
};

const fill = (tpl, p) => tpl.replace(/\{name\}/g, p.name).replace(/\{team\}/g, p.team || 'FA');

/**
 * Build a deterministic sample news feed for a roster's players. Returns items
 * in the same shape the roster news UI expects:
 *   { id, playerId, playerName, Title, Content, Updated }
 * Injured players get injury news; others get beat notes. Stable ordering
 * (most recent first) driven by player index so it doesn't churn across renders.
 */
export const buildSampleNewsForPlayers = (players) => {
  if (!Array.isArray(players) || players.length === 0) return [];
  const now = Date.now();
  const items = [];
  players.forEach((p, i) => {
    if (!p || !p.id) return;
    const status = p.status && p.status !== 'active' ? p.status : null;
    let pool;
    let tag; // 'injury' | 'lineup' | 'transaction'
    if (status && INJURY_NEWS[status]) {
      pool = INJURY_NEWS[status];
      tag = status === 'MiLB' ? 'transaction' : 'injury';
    } else if (isPitcher(p)) {
      pool = PITCHER_NEWS;
      tag = i % 2 === 0 ? 'lineup' : 'transaction';
    } else {
      pool = HITTER_NEWS;
      tag = i % 3 === 0 ? 'lineup' : null;
    }
    // Injured players always get news; others get news for ~2/3 of the roster
    // so the feed feels selective rather than one-per-player.
    if (!status && i % 3 === 2) return;
    const tpl = pool[i % pool.length];
    // Space timestamps out over the last ~3 days, newest for injured players.
    const hoursAgo = status ? (i % 6) + 1 : (i * 5) % 72 + 2;
    const posList = p.positions || (p.position ? [p.position] : []);
    items.push({
      id: `fx-news-${p.id}-${i}`,
      playerId: p.id,
      playerName: p.name,
      playerTeam: p.team || 'FA',
      playerPos: (posList[0] || '').replace('UTIL', '') || posList[0] || '',
      tag,
      Title: fill(tpl.title, p),
      Content: fill(tpl.content, p),
      Source: 'Fantasy Wire',
      Updated: new Date(now - hoursAgo * 3600 * 1000).toISOString(),
      hot: !!status,
    });
  });
  items.sort((a, b) => new Date(b.Updated) - new Date(a.Updated));
  return items.slice(0, 25);
};

export default {
  USE_ROSTER_FIXTURES,
  VARIATIONS,
  VARIATION_KEYS,
  pickVariationForTeam,
  resolveVariationKey,
  getVariationOverride,
  setVariationOverride,
  getFixtureRoster,
  saveFixtureRoster,
  buildSampleNewsForPlayers,
};
